// Owner naming and ordering, mirroring the server's User.display_name and
// User.sort_by_display_name so the client never reorders what the server listed.

/** Shown wherever an owner has no profile name; a raw user id is never shown. */
export const UNNAMED_OWNER = "Unnamed owner";

/** Trimmed "first last", or null when both are blank (the server's display_name). */
export const ownerNameFrom = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string | null => `${firstName ?? ""} ${lastName ?? ""}`.trim() || null;

export const ownerLabel = (name: string | null): string => name ?? UNNAMED_OWNER;

type OwnerProfileNames = {
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

/** fullName, else first + last, else UNNAMED_OWNER; a blank fullName counts as missing. */
export const profileOwnerLabel = (profile: OwnerProfileNames | null | undefined): string =>
  ownerLabel(profile?.fullName?.trim() || ownerNameFrom(profile?.firstName, profile?.lastName));

// Accents and case ignored; letters that don't decompose (Ø, Ł, ß) keep their
// code point, so ø and ł sort after z exactly as on the server.
const sortName = (name: string) => name.normalize("NFD").replace(/\p{Mn}/gu, "").toLowerCase();

// Elixir compares binaries byte-wise, i.e. by code point; JS `<` compares UTF-16
// units, which disagrees past the BMP.
const compareCodePoints = (a: string, b: string): number => {
  const left = Array.from(a);
  const right = Array.from(b);
  for (let index = 0; index < Math.min(left.length, right.length); index += 1) {
    const diff = (left[index].codePointAt(0) ?? 0) - (right[index].codePointAt(0) ?? 0);
    if (diff !== 0) return diff;
  }
  return left.length - right.length;
};

/** Named owners by accent- and case-insensitive name, nameless owners last; ties by id. */
export const compareOwners = (
  a: { id: string; name: string | null },
  b: { id: string; name: string | null },
): number => {
  if (a.name === null || b.name === null) {
    if (a.name !== b.name) return a.name === null ? 1 : -1;
  } else {
    const byName = compareCodePoints(sortName(a.name), sortName(b.name));
    if (byName !== 0) return byName;
  }
  return compareCodePoints(a.id, b.id);
};
